/**
 * AutoCaptions tool — Phase 2:
 *   • Anonymous, 3-step flow (upload → transcribe → style → download).
 *   • Live in-browser caption preview synced to <video>.currentTime so the
 *     user sees their style change instantly (no server re-render needed).
 *   • Full custom style controls — font size, colours, position, words/line,
 *     uppercase, animation type. Sent as `style_override` at render time.
 *   • Language picker (30 Whisper-supported codes + auto-detect).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload, Loader2, Sparkles, Wand2, AlertTriangle, Download,
  Film, FileText, CheckCircle2, RotateCcw, Play, Settings2, Languages,
  Smile, FileUp, History, Share2, Aperture, LogIn, Scissors, Palette,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { PresetCard, WordChip } from "./_PresetCard";
import { BeatWaveform, KeyframeEditor } from "./_Phase35Widgets";
import "./AutoCaptionsPage.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

const STEPS = [
  { key: "upload", label: "Upload" },
  { key: "transcribe", label: "Transcribe" },
  { key: "style", label: "Style" },
  { key: "result", label: "Download" },
];

const stepIndex = (status) => {
  if (!status || status === "uploaded") return 0;
  if (status === "transcribing") return 1;
  if (status === "ready_to_edit") return 2;
  if (status === "rendering") return 2;
  if (status === "done") return 3;
  return 0;
};

const ASPECTS = [
  { key: "16:9", label: "16:9", hint: "Landscape" },
  { key: "9:16", label: "9:16", hint: "Reels / Shorts" },
  { key: "1:1", label: "1:1", hint: "Square" },
];

const TRANSLATE_TARGETS = [
  { code: "none", name: "Keep original" },
  { code: "en", name: "Translate → English" },
  { code: "hi", name: "Translate → Hindi" },
  { code: "hinglish", name: "Translate → Hinglish (Roman)" },
  { code: "es", name: "Translate → Spanish" },
  { code: "fr", name: "Translate → French" },
  { code: "de", name: "Translate → German" },
  { code: "pt", name: "Translate → Portuguese" },
  { code: "ja", name: "Translate → Japanese" },
  { code: "ar", name: "Translate → Arabic" },
];

const POSITIONS = [
  { key: "bottom", label: "Bottom" },
  { key: "center", label: "Center" },
  { key: "top", label: "Top" },
];

const ALIGNMENTS = [
  { key: "left", label: "Left" },
  { key: "center", label: "Center" },
  { key: "right", label: "Right" },
];

const FONT_FAMILIES = [
  // Display / viral
  "Montserrat", "Anton", "Bebas Neue", "Oswald", "Poppins", "Playfair Display",
  // Devanagari-safe
  "Mukta", "Hind", "Noto Sans Devanagari", "Noto Serif Devanagari",
  // System fallbacks
  "FreeSans", "DejaVu Sans",
];

const ANIMATIONS = [
  { key: "karaoke", label: "Karaoke" },
  { key: "pop_scale", label: "Pop scale" },
  { key: "bounce", label: "Bounce" },
  { key: "pop_glow", label: "Pop + glow" },
  { key: "fade", label: "Fade" },
  { key: "none", label: "None" },
];

export default function AutoCaptionsPage() {
  const navigate = useNavigate();
  const { projectId: routeProjectId } = useParams();
  const [project, setProject] = useState(null);
  const [presets, setPresets] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState("bold_karaoke");
  const [custom, setCustom] = useState(null);             // overrides on top of preset
  const [language, setLanguage] = useState("auto");
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [previewT, setPreviewT] = useState(0);
  const [emojiBusy, setEmojiBusy] = useState(false);
  const [aspect, setAspect] = useState("16:9");
  const [targetLang, setTargetLang] = useState("none");
  const [isAuthed, setIsAuthed] = useState(null);
  const [splitting, setSplitting] = useState(false);
  const [sceneClips, setSceneClips] = useState(null);
  const [tryingSample, setTryingSample] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  const [findTerm, setFindTerm] = useState("");
  const [replaceTerm, setReplaceTerm] = useState("");
  const [beats, setBeats] = useState(null);
  const [beatBusy, setBeatBusy] = useState(false);
  const [abVariants, setAbVariants] = useState(null);
  const [abBusy, setAbBusy] = useState(false);
  const [abPresetB, setAbPresetB] = useState("viral_yellow");
  const [customFont, setCustomFont] = useState(null);
  const fontInput = useRef(null);
  const [keyframes, setKeyframes] = useState([]);  // keyframes for cue index 0
  const [advancedBusy, setAdvancedBusy] = useState(false);
  const [advancedEffect, setAdvancedEffect] = useState("gradient");
  const [recommendedPreset, setRecommendedPreset] = useState(null);
  const fileInput = useRef(null);
  const srtInput = useRef(null);
  const videoRef = useRef(null);
  const pollRef = useRef(null);

  // ── Fetch presets + languages once ─────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/api/auto-captions/presets`)
      .then((r) => setPresets(r.data.presets || []))
      .catch(() => setPresets([]));
    axios.get(`${API}/api/auto-captions/languages`)
      .then((r) => setLanguages(r.data.languages || []))
      .catch(() => setLanguages([]));
    // Cheap auth probe — uses the `authenticated` flag returned by my-projects
    // (no 401 noise on anonymous visits).
    axios.get(`${API}/api/auto-captions/my-projects`, {
      withCredentials: true,
    })
      .then((r) => setIsAuthed(!!r.data?.authenticated))
      .catch(() => setIsAuthed(false));
  }, []);

  // If the route already has a project id, hydrate from the backend instead
  // of showing the upload step (used when resuming from history).
  useEffect(() => {
    if (!routeProjectId) return;
    axios.get(`${API}/api/auto-captions/projects/${routeProjectId}`)
      .then((r) => setProject(r.data))
      .catch(() => setError("That project could not be loaded."));
  }, [routeProjectId]);

  // Once we have a project_id, lift it into the URL so the user can refresh,
  // share, and (importantly) survive a sign-in/sign-up roundtrip without
  // losing context — the SignInRequiredCard captures window.location.pathname
  // as the post-auth redirect target.
  useEffect(() => {
    if (!project?.project_id) return;
    const desired = `/tools/auto-captions/projects/${project.project_id}`;
    if (window.location.pathname !== desired) {
      navigate(desired, { replace: true });
    }
  }, [project?.project_id, navigate]);

  // Seed custom controls when preset changes — keep them tracking the preset
  // defaults until the user starts dragging sliders, at which point the
  // override sticks for that preset.
  useEffect(() => {
    const p = presets.find((x) => x.key === selectedPreset);
    if (!p) return;
    setCustom({ ...p.defaults });
  }, [selectedPreset, presets]);

  // Poll project state while the backend is working.
  useEffect(() => {
    if (!project?.project_id) return;
    if (["uploaded", "ready_to_edit", "done", "failed"].includes(project.status)) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/auto-captions/projects/${project.project_id}`);
        setProject(r.data);
        if (["ready_to_edit", "done", "failed"].includes(r.data.status)) {
          clearInterval(pollRef.current);
        }
      } catch {/* keep polling */ }
    }, 1800);
    return () => clearInterval(pollRef.current);
  }, [project?.status, project?.project_id]);

  // ── File pick / drop ──────────────────────────────────────────────
  // We always go through the chunked-upload endpoints. A single 100+ MB
  // multipart POST is blocked by most ingress proxies (Kubernetes nginx-ingress
  // defaults to ~32 MB and returns 413). Chunking sidesteps that entirely.
  const onFiles = async (files) => {
    setError(null);
    const file = files && files[0];
    if (!file) return;
    const MB = 1024 * 1024;
    if (file.size > 200 * MB) {
      setError("File too large — max 200 MB.");
      return;
    }
    setUploading(true); setUploadPct(0);
    try {
      // 1) Init the project on the server.
      const init = await axios.post(`${API}/api/auto-captions/projects/init-chunked`, {
        filename: file.name,
        total_bytes: file.size,
      }, { withCredentials: true });
      const { project_id, chunk_size } = init.data;

      // 2) Upload chunks sequentially. Sequential keeps the assembled file
      // bytes-in-order without server-side stitching.
      const totalChunks = Math.max(1, Math.ceil(file.size / chunk_size));
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunk_size;
        const end = Math.min(file.size, start + chunk_size);
        const slice = file.slice(start, end);
        const fd = new FormData();
        fd.append("file", slice, file.name);
        fd.append("chunk_index", String(i));
        fd.append("total_chunks", String(totalChunks));
        await axios.post(
          `${API}/api/auto-captions/projects/${project_id}/chunk`,
          fd,
          {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (e) => {
              const chunkPct = (e.loaded * 100) / (e.total || (end - start));
              const overall = ((i * 100) + chunkPct) / totalChunks;
              setUploadPct(Math.min(99, Math.round(overall)));
            },
          },
        );
      }

      // 3) Finalize — probes the assembled file + pushes to R2.
      const finalize = await axios.post(
        `${API}/api/auto-captions/projects/${project_id}/finalize-upload`,
        {}, { withCredentials: true },
      );
      setUploadPct(100);
      setProject(finalize.data);

      // 4) Auto-kick transcription with selected language.
      const t = await axios.post(
        `${API}/api/auto-captions/projects/${finalize.data.project_id}/transcribe`,
        { language: language === "auto" ? null : language },
      );
      setProject(t.data);
      toast.success("Upload complete — transcribing now…");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Upload failed";
      setError(msg); toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const editWord = (idx, newText) => {
    if (!project?.transcript?.words) return;
    const words = [...project.transcript.words];
    words[idx] = { ...words[idx], word: newText };
    setProject({ ...project, transcript: { ...project.transcript, words } });
  };

  const deleteWord = (idx) => {
    if (!project?.transcript?.words) return;
    const words = project.transcript.words.filter((_, i) => i !== idx);
    setProject({ ...project, transcript: { ...project.transcript, words } });
  };

  const runFindReplace = () => {
    if (!findTerm || !project?.transcript?.words) return;
    const needle = findTerm;
    const re = new RegExp(needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    const words = project.transcript.words.map((w) => ({
      ...w,
      word: (w.word || "").replace(re, replaceTerm),
    }));
    const hits = project.transcript.words.filter((w) => re.test(w.word || "")).length;
    setProject({ ...project, transcript: { ...project.transcript, words } });
    toast.success(`Replaced in ${hits} word${hits === 1 ? "" : "s"}`);
    setFindOpen(false);
    setFindTerm("");
    setReplaceTerm("");
  };

  const detectBeats = async () => {
    if (!project?.project_id) return;
    setBeatBusy(true);
    try {
      const r = await axios.post(`${API}/api/auto-captions/projects/${project.project_id}/detect-beats`);
      setBeats(r.data);
      toast.success(`Detected ${r.data.beats?.length || 0} beats @ ${r.data.tempo_bpm} BPM`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Beat detection failed");
    } finally {
      setBeatBusy(false);
    }
  };

  const snapToBeats = async () => {
    if (!project?.project_id) return;
    setBeatBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/auto-captions/projects/${project.project_id}/snap-to-beats`,
        { tolerance_sec: 0.25 },
      );
      toast.success(`Snapped ${r.data.snapped}/${r.data.total_words} words to beat`);
      // Refetch project to get new word timings
      const fresh = await axios.get(`${API}/api/auto-captions/projects/${project.project_id}`);
      setProject(fresh.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Beat snap failed");
    } finally {
      setBeatBusy(false);
    }
  };

  const uploadProjectFont = async (file) => {
    if (!project?.project_id || !file) return;
    const form = new FormData();
    form.append("font_file", file);
    try {
      const r = await axios.post(
        `${API}/api/auto-captions/projects/${project.project_id}/fonts`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setCustomFont(r.data);
      setCustom((c) => ({ ...c, font: r.data.family }));
      toast.success(`Font "${r.data.family}" ready for this project`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Font upload failed");
    }
  };

  const kickAbRender = async () => {
    if (!project?.project_id) return;
    setAbBusy(true);
    setAbVariants(null);
    try {
      const r = await axios.post(
        `${API}/api/auto-captions/projects/${project.project_id}/ab-render`,
        { preset_a: selectedPreset, preset_b: abPresetB, aspect },
      );
      // Poll each variant until done
      const variants = r.data.variants;
      const poll = async (vid) => {
        for (let i = 0; i < 60; i++) {
          const vr = await axios.get(`${API}/api/auto-captions/projects/${vid}`);
          if (vr.data.status === "done" || vr.data.status === "failed") return vr.data;
          await new Promise((res) => setTimeout(res, 4000));
        }
        return null;
      };
      const [a, b] = await Promise.all([poll(variants[0].variant_id), poll(variants[1].variant_id)]);
      setAbVariants([a, b].filter(Boolean));
      toast.success("A/B variants ready — pick your favourite!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "A/B render failed");
    } finally {
      setAbBusy(false);
    }
  };

  const recordAbPick = async (variant, pickedIdx) => {
    const other = abVariants[pickedIdx === 0 ? 1 : 0];
    try {
      await axios.post(`${API}/api/auto-captions/ab/pick`, {
        parent_project_id: project.project_id,
        picked_variant_id: variant.project_id,
        picked_preset: variant.selected_preset,
        rejected_preset: other?.selected_preset || "",
        aspect,
      });
    } catch { /* non-blocking */ }
  };

  const kickAdvancedRender = async () => {
    if (!project?.project_id) return;
    setAdvancedBusy(true);
    try {
      const payload = {
        preset: selectedPreset,
        effect: advancedEffect,
        aspect,
        fps: 20,
        duration_sec: 20,
        style_override: keyframes.length ? { keyframes: { "0": keyframes } } : null,
      };
      await axios.post(`${API}/api/auto-captions/projects/${project.project_id}/advanced-render`, payload);
      toast.success(`Advanced ${advancedEffect} render queued — refresh in 2-3 minutes.`);
      // Poll for completion
      for (let i = 0; i < 60; i++) {
        await new Promise((res) => setTimeout(res, 5000));
        const fresh = await axios.get(`${API}/api/auto-captions/projects/${project.project_id}`);
        setProject(fresh.data);
        if (fresh.data.status === "done" || fresh.data.status === "failed") break;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Advanced render failed");
    } finally {
      setAdvancedBusy(false);
    }
  };

  // Fetch recommended preset once per session
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/auto-captions/ab/recommend`);
        if (r.data?.scope !== "default") setRecommendedPreset(r.data);
      } catch { /* silent */ }
    })();
  }, []);

  // Reading speed estimate: words per minute. CPS warning when > 20 chars/sec
  // (industry subtitle readability cap) — flags any 2 s window that is too dense.
  const readingStats = useMemo(() => {
    const words = project?.transcript?.words || [];
    if (words.length < 2) return { wpm: 0, dense: 0 };
    const totalDur = Math.max(
      0.1,
      (words[words.length - 1]?.end || 0) - (words[0]?.start || 0),
    );
    const wpm = Math.round((words.length / totalDur) * 60);
    let dense = 0;
    for (const w of words) {
      const len = (w.word || "").length;
      const dur = Math.max(0.05, (w.end || 0) - (w.start || 0));
      if (len / dur > 20) dense++;
    }
    return { wpm, dense };
  }, [project]);

  const saveTranscript = async () => {
    if (!project?.project_id) return;
    try {
      await axios.put(`${API}/api/auto-captions/projects/${project.project_id}/transcript`, {
        words: project.transcript.words,
      });
    } catch {/* silent */ }
  };

  const generate = async () => {
    if (!project?.project_id) return;
    await saveTranscript();
    setError(null);
    try {
      const r = await axios.post(`${API}/api/auto-captions/projects/${project.project_id}/render`, {
        preset: selectedPreset,
        style_override: custom,
        aspect,
        target_language: targetLang,
      });
      setProject(r.data);
      toast.success("Rendering captions — this takes a few seconds…");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Render failed";
      setError(msg); toast.error(msg);
    }
  };

  const copyShareLink = async () => {
    if (!project?.share_slug) return;
    const url = `${window.location.origin}/c/${project.share_slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied!");
    } catch {
      toast.error("Couldn't copy — link: " + url);
    }
  };

  const tryWithSample = async () => {
    setError(null);
    setTryingSample(true);
    try {
      const r = await axios.post(`${API}/api/auto-captions/projects/try-sample`, {}, {
        withCredentials: true,
      });
      setProject(r.data);
      toast.success("Sample ready — captioned in 0 seconds ✨");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Could not load sample";
      setError(msg); toast.error(msg);
    } finally {
      setTryingSample(false);
    }
  };

  const splitIntoScenes = async () => {
    if (!project?.project_id) return;
    setSplitting(true);
    try {
      const r = await axios.post(`${API}/api/auto-captions/projects/${project.project_id}/split-scenes`);
      setSceneClips(r.data.clips || []);
      if (!(r.data.clips || []).length) {
        toast.message("Only one scene detected — try a longer clip.");
      } else {
        toast.success(`Found ${r.data.clips.length} scenes — clips ready below.`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Scene split failed";
      toast.error(msg);
    } finally {
      setSplitting(false);
    }
  };

  const reset = () => {
    setProject(null); setError(null); setUploadPct(0); setUploading(false);
    setSelectedPreset("bold_karaoke"); setShowAdvanced(false); setPreviewT(0);
  };

  // Import an SRT/VTT file to skip transcription entirely. Still needs a
  // project to attach to, so we kick off an upload first then chain the SRT
  // import once the project is created.
  const onSrtPick = async (e) => {
    const srtFile = e.target.files && e.target.files[0];
    if (!srtFile) return;
    if (!project?.project_id) {
      setError("Upload a video first, then attach your SRT.");
      e.target.value = "";
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", srtFile);
      const r = await axios.post(
        `${API}/api/auto-captions/projects/${project.project_id}/import-srt`,
        fd, { headers: { "Content-Type": "multipart/form-data" } },
      );
      setProject(r.data);
      toast.success("SRT imported — transcription skipped");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "SRT import failed";
      setError(msg); toast.error(msg);
    } finally {
      e.target.value = "";
    }
  };

  const runAutoEmoji = async () => {
    if (!project?.project_id) return;
    setEmojiBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/auto-captions/projects/${project.project_id}/auto-emoji`,
        { words_per_line: custom?.words_per_line || 4 },
      );
      setProject(r.data);
      toast.success("Emojis added — preview them on the left");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Auto-emoji failed";
      setError(msg); toast.error(msg);
    } finally {
      setEmojiBusy(false);
    }
  };

  // ── Derived UI state ─────────────────────────────────────────────
  const status = project?.status || "uploaded";
  const activeStep = stepIndex(status);
  const sourceUrl = project?.source_url ? `${API}${project.source_url}` : null;

  // Compute caption to display at current preview time.
  // Words → caption "line" (n words per line). Active word within the line is
  // the one whose [start, end] contains currentTime.
  const currentLine = useMemo(() => {
    if (!project?.transcript?.words?.length || !custom) return null;
    const wpl = custom.words_per_line || 4;
    const words = project.transcript.words;
    // Build buckets the same way the backend will.
    let bucket = [];
    let idxStart = 0;
    for (let i = 0; i < words.length; i++) {
      bucket.push(words[i]);
      if (bucket.length >= wpl || i === words.length - 1) {
        const start = bucket[0].start;
        const end = bucket[bucket.length - 1].end;
        if (previewT >= start - 0.15 && previewT <= end + 0.15) {
          return { words: bucket, start, end, idxStart };
        }
        idxStart = i + 1;
        bucket = [];
      }
    }
    return null;
  }, [project?.transcript?.words, custom, previewT]);

  return (
    <>
      <Header />
      <div className="ac-page" data-testid="auto-captions-page">
        <div className="ac-inner">
          <div className="ac-top-row">
            <div>
              <span className="ac-eyebrow"><Sparkles size={12} /> AutoCaptions · Sarvam-powered Hinglish &amp; Indic AI</span>
              <h1 className="ac-h1">Upload. Style. <em>Get captions, instantly.</em></h1>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="ac-btn ac-btn--ghost"
                onClick={() => navigate("/tools/auto-captions/brand-kit")}
                data-testid="ac-brand-kit-link"
                title="Logo + custom font for agencies"
              >
                <Palette size={14}/> Brand Kit
              </button>
              <button
                type="button"
                className="ac-btn ac-btn--ghost"
                onClick={() => navigate("/tools/auto-captions/history")}
                data-testid="ac-history-link"
              >
                <History size={14}/> My projects
              </button>
            </div>
          </div>
          <p className="ac-sub">
            Upload a video and we&rsquo;ll burn animated, word-by-word captions onto a new MP4
            — plus give you the SRT so you can use it anywhere. <strong>30+ languages supported.</strong>
          </p>

          {/* Step strip */}
          <div className="ac-steps" data-testid="ac-steps">
            {STEPS.map((s, i) => (
              <div key={s.key} className="ac-step" data-active={i === activeStep} data-done={i < activeStep}>
                <span className="ac-step-num">{i < activeStep ? <CheckCircle2 size={14} /> : i + 1}</span>
                <span>{s.label}</span>
                {i < STEPS.length - 1 && <span className="ac-step-sep" />}
              </div>
            ))}
          </div>

          {error && (
            <div className="ac-error" data-testid="ac-error">
              <AlertTriangle size={16} /> {error}
            </div>
          )}

          <AnimatePresence mode="wait">
            {/* ── Step 1: Upload ── */}
            {!project && (
              <motion.div
                key="upload"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.32 }}
                className="ac-card"
              >
                {/* Language picker */}
                <div className="ac-lang-row" data-testid="ac-lang-row">
                  <label className="ac-lang-label"><Languages size={14}/> Language</label>
                  <select
                    className="ac-lang-select"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    data-testid="ac-lang-select"
                  >
                    {languages.map((l) => (
                      <option key={l.code} value={l.code}>{l.name}</option>
                    ))}
                  </select>
                </div>

                <div
                  className="ac-drop"
                  data-dragging={dragging}
                  data-testid="ac-dropzone"
                  onClick={() => !uploading && fileInput.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => { e.preventDefault(); setDragging(false); onFiles(e.dataTransfer.files); }}
                >
                  <input
                    ref={fileInput}
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm,video/x-matroska,.mp4,.mov,.m4v,.webm,.mkv"
                    hidden
                    onChange={(e) => onFiles(e.target.files)}
                    data-testid="ac-file-input"
                  />
                  <div className="ac-drop-icon">
                    {uploading ? <Loader2 size={28} className="ac-spin" /> : <Upload size={28} />}
                  </div>
                  <p className="ac-drop-title">
                    {uploading ? "Uploading…" : dragging ? "Drop the video here" : "Drop a video to caption"}
                  </p>
                  <p className="ac-drop-sub">
                    or <strong>click to browse</strong>. We&rsquo;ll handle transcription &amp; rendering.
                  </p>
                  <div className="ac-drop-meta">
                    <span><Film size={12} /> MP4 · MOV · WEBM · MKV</span>
                    <span>Up to 5 min</span>
                    <span>Max 200 MB</span>
                  </div>
                  {uploading && (
                    <>
                      <div className="ac-upload-progress"><span style={{ width: `${uploadPct}%` }} /></div>
                      <p className="ac-upload-status">{uploadPct}% uploaded</p>
                    </>
                  )}
                </div>

                <p className="ac-tip">
                  💡 Already have a transcript? Upload the video here, then{" "}
                  <strong>attach an SRT in the next step</strong> to skip transcription.
                </p>

                <div className="ac-sample-row">
                  <span className="ac-sample-or">or</span>
                  <button
                    type="button"
                    className="ac-btn ac-btn--sample"
                    onClick={tryWithSample}
                    disabled={tryingSample || uploading}
                    data-testid="ac-try-sample"
                  >
                    {tryingSample ? (
                      <><Loader2 size={14} className="ac-spin" /> Loading sample…</>
                    ) : (
                      <><Play size={14} /> Try with a sample video — captioned in 0 seconds</>
                    )}
                  </button>
                </div>
              </motion.div>
            )}

            {/* ── Step 2: Transcribing ── */}
            {project && status === "transcribing" && (
              <motion.div key="transcribe" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="ac-card">
                <div className="ac-progress-hero">
                  <div className="ac-progress-spinner"><Wand2 size={22} /></div>
                  <p className="ac-progress-title">{project.stage_message || "Transcribing…"}</p>
                  <p className="ac-progress-sub">Aapka audio analyse ho raha hai. 30 sec mein ho jayega.</p>
                  <div className="ac-progress-bar"><span style={{ width: `${project.progress || 30}%` }} /></div>
                </div>
              </motion.div>
            )}

            {/* ── Step 3: Editor + Live Preview + Style Panel ── */}
            {project && status === "ready_to_edit" && custom && (
              <motion.div key="edit" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="ac-editor-grid-3">

                  {/* LEFT — Live preview */}
                  <div className="ac-panel ac-preview-panel" data-testid="ac-preview">
                    <div className="ac-panel-h">
                      <p className="ac-panel-title">Live preview</p>
                      <span className="ac-panel-meta">{(previewT || 0).toFixed(1)}s</span>
                    </div>
                    <div className="ac-video-wrap">
                      {sourceUrl && (
                        <video
                          ref={videoRef}
                          src={sourceUrl}
                          controls
                          playsInline
                          onTimeUpdate={(e) => setPreviewT(e.currentTarget.currentTime)}
                          onLoadedMetadata={(e) => setPreviewT(e.currentTarget.currentTime)}
                          data-testid="ac-preview-video"
                        />
                      )}
                      {currentLine && (
                        <CaptionOverlay line={currentLine} style={custom} t={previewT} />
                      )}
                    </div>
                    <p className="ac-preview-hint">Captions yahan live update hote hain — jab tak final render nahi karein.</p>
                  </div>

                  {/* RIGHT — Style + Transcript */}
                  <div className="ac-side">
                    <div className="ac-panel" data-testid="ac-presets">
                      <div className="ac-panel-h">
                        <p className="ac-panel-title">Style</p>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            type="button"
                            className="ac-link"
                            onClick={runAutoEmoji}
                            disabled={emojiBusy}
                            data-testid="ac-auto-emoji"
                            title="Add an emoji to each caption line"
                          >
                            {emojiBusy ? <Loader2 size={12} className="ac-spin"/> : <Smile size={12} />}
                            {emojiBusy ? "Adding…" : "Auto-emoji"}
                          </button>
                          <button
                            type="button"
                            className="ac-link"
                            onClick={() => setShowAdvanced((v) => !v)}
                            data-testid="ac-advanced-toggle"
                          >
                            <Settings2 size={12} /> {showAdvanced ? "Hide" : "Advanced"}
                          </button>
                        </div>
                      </div>
                      <div className="ac-presets ac-presets--compact">
                        {presets.map((p) => (
                          <PresetCard
                            key={p.key}
                            preset={p}
                            selected={selectedPreset === p.key}
                            onSelect={setSelectedPreset}
                          />
                        ))}
                      </div>

                      {showAdvanced && (
                        <CustomControls value={custom} onChange={setCustom} />
                      )}

                      {/* Phase 4: aspect ratio + translation + watermark hint */}
                      <div className="ac-extra-row" data-testid="ac-extra-row">
                        <div>
                          <p className="ac-extra-label"><Aperture size={11}/> Aspect</p>
                          <div className="ac-pill-group" role="radiogroup" data-testid="ac-aspect-group">
                            {ASPECTS.map((a) => (
                              <button
                                key={a.key}
                                type="button"
                                className="ac-pill"
                                data-active={aspect === a.key}
                                onClick={() => setAspect(a.key)}
                                data-testid={`ac-aspect-${a.key.replace(":","-")}`}
                                title={a.hint}
                              >{a.label}</button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="ac-extra-label"><Languages size={11}/> Translation</p>
                          <select
                            className="ac-lang-select"
                            value={targetLang}
                            onChange={(e) => setTargetLang(e.target.value)}
                            data-testid="ac-translate-select"
                            style={{ width: "100%" }}
                          >
                            {TRANSLATE_TARGETS.map((t) => (
                              <option key={t.code} value={t.code}>{t.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {isAuthed === false && (
                        <p className="ac-wm-badge" data-testid="ac-watermark-hint">
                          ⚠️ Anonymous renders include a small watermark.
                          <button
                            type="button"
                            className="ac-link"
                            onClick={() => navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
                            style={{ marginLeft: 6 }}
                          >
                            Sign in
                          </button>{" "}
                          for clean exports.
                        </p>
                      )}
                    </div>

                    {/* ── Phase 3 — Beat sync, custom font, A/B render ── */}
                    <div className="ac-panel ac-phase3" data-testid="ac-phase3">
                      <div className="ac-panel-h">
                        <p className="ac-panel-title">⚡ Pro tools</p>
                      </div>
                      <div className="ac-phase3-grid">
                        <div className="ac-phase3-cell">
                          <p className="ac-phase3-label">🎵 Beat sync</p>
                          <p className="ac-phase3-desc">
                            {beats
                              ? `${beats.beats?.length || 0} beats · ${beats.tempo_bpm} BPM`
                              : "Detect music beats, then snap captions to the drop."}
                          </p>
                          {beats && beats.peaks && beats.peaks.length > 0 && (
                            <div style={{ marginBottom: 8 }}>
                              <BeatWaveform
                                peaks={beats.peaks}
                                beats={beats.beats || []}
                                duration={beats.duration_sec || 0}
                              />
                            </div>
                          )}
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button
                              type="button"
                              className="ac-btn ac-btn--ghost ac-btn--sm"
                              onClick={detectBeats}
                              disabled={beatBusy}
                              data-testid="ac-beat-detect"
                            >
                              {beatBusy ? "Working…" : (beats ? "Re-analyse" : "Detect beats")}
                            </button>
                            {beats && (
                              <button
                                type="button"
                                className="ac-btn ac-btn--accent ac-btn--sm"
                                onClick={snapToBeats}
                                disabled={beatBusy}
                                data-testid="ac-beat-snap"
                              >
                                Snap to beat
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="ac-phase3-cell">
                          <p className="ac-phase3-label">🔤 Project font</p>
                          <p className="ac-phase3-desc">
                            {customFont
                              ? `${customFont.family} — active for this video`
                              : "Upload .ttf, .otf, .woff, .woff2 (≤ 8 MB)"}
                          </p>
                          <button
                            type="button"
                            className="ac-btn ac-btn--ghost ac-btn--sm"
                            onClick={() => fontInput.current?.click()}
                            data-testid="ac-project-font-pick"
                          >
                            {customFont ? "Swap font" : "Upload font"}
                          </button>
                          <input
                            ref={fontInput}
                            type="file"
                            accept=".ttf,.otf,.woff,.woff2"
                            hidden
                            data-testid="ac-project-font-input"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) uploadProjectFont(f);
                              e.target.value = "";
                            }}
                          />
                        </div>

                        <div className="ac-phase3-cell">
                          <p className="ac-phase3-label">🅰️🅱️ A/B preview</p>
                          <p className="ac-phase3-desc">Render two preset variants side-by-side.</p>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                            <select
                              className="ac-select ac-select--sm"
                              value={abPresetB}
                              onChange={(e) => setAbPresetB(e.target.value)}
                              data-testid="ac-ab-select-b"
                            >
                              {presets
                                .filter((p) => p.key !== selectedPreset)
                                .map((p) => (
                                  <option key={p.key} value={p.key}>{p.name}</option>
                                ))}
                            </select>
                            <button
                              type="button"
                              className="ac-btn ac-btn--accent ac-btn--sm"
                              onClick={kickAbRender}
                              disabled={abBusy}
                              data-testid="ac-ab-run"
                            >
                              {abBusy ? "Rendering 2 variants…" : "Generate A/B"}
                            </button>
                          </div>
                        </div>
                      </div>

                      {abVariants && abVariants.length === 2 && (
                        <div className="ac-ab-result" data-testid="ac-ab-result">
                          {abVariants.map((v, i) => (
                            <div key={v.project_id} className="ac-ab-tile">
                              <p className="ac-ab-label">Variant {i === 0 ? "A" : "B"} · {v.selected_preset}</p>
                              <video
                                src={`${API}${v.outputs?.mp4}`}
                                controls
                                playsInline
                                className="ac-ab-video"
                                data-testid={`ac-ab-video-${i}`}
                              />
                              <a
                                href={`${API}${v.outputs?.mp4}`}
                                download
                                className="ac-btn ac-btn--primary-xl ac-btn--sm"
                                onClick={() => recordAbPick(v, i)}
                                data-testid={`ac-ab-pick-${i}`}
                              >
                                Pick this one
                              </a>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* ── Phase 3.5 — Keyframes + Advanced render ── */}
                    <div className="ac-panel ac-phase35" data-testid="ac-phase35">
                      <div className="ac-panel-h">
                        <p className="ac-panel-title">🎬 Advanced render <span style={{fontSize:11, color:"var(--ac-text-soft)", marginLeft:8}}>(2-3 min · chromium pipeline)</span></p>
                      </div>
                      {recommendedPreset && (
                        <div className="ac-reco" data-testid="ac-recommended-preset">
                          💡 Based on {recommendedPreset.scope === "personal" ? "your past picks" : "what other creators pick"},
                          <b> {recommendedPreset.recommended_preset}</b> performs best ({recommendedPreset.pick_count} picks).
                        </div>
                      )}
                      <KeyframeEditor
                        keyframes={keyframes}
                        durationMs={2000}
                        onChange={setKeyframes}
                      />
                      <div className="ac-phase35-run">
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <label className="ac-kf-l" style={{ margin: 0 }}>Effect</label>
                          <select
                            className="ac-select ac-select--sm"
                            value={advancedEffect}
                            onChange={(e) => setAdvancedEffect(e.target.value)}
                            data-testid="ac-advanced-effect"
                          >
                            <option value="gradient">Animated gradient</option>
                            <option value="glow">Glow pulse</option>
                            <option value="extrude">3-D extrude</option>
                            <option value="plain">Plain (no CSS effect)</option>
                          </select>
                          <button
                            type="button"
                            className="ac-btn ac-btn--primary-xl ac-btn--sm"
                            onClick={kickAdvancedRender}
                            disabled={advancedBusy}
                            data-testid="ac-advanced-run"
                          >
                            {advancedBusy ? "Rendering frames…" : "Run advanced render"}
                          </button>
                        </div>
                        <p className="ac-phase35-note">
                          Uses a headless-Chromium frame pipeline for effects ASS can't do:
                          animated gradients, glow breathing, 3-D extrude, keyframed scale/opacity.
                          Slower than the default render — use when polish &gt; speed.
                        </p>
                      </div>
                      {(project.advanced_renders || []).length > 0 && (
                        <div className="ac-phase35-outputs" data-testid="ac-advanced-outputs">
                          {(project.advanced_renders || []).map((r, i) => (
                            <a
                              key={i}
                              href={`${API}${r.url}`}
                              download
                              className="ac-download"
                              data-testid={`ac-advanced-dl-${r.effect}`}
                            >
                              <span className="ac-download-icon" style={{ background: "linear-gradient(135deg,#a855f7,#ec4899)" }}>🎬</span>
                              <div className="ac-download-info">
                                <p className="ac-download-title">Advanced · {r.effect}</p>
                                <p className="ac-download-sub">Preset {r.preset} · chromium render</p>
                              </div>
                              <Download size={16} color="rgba(255,255,255,0.7)"/>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="ac-panel" data-testid="ac-transcript">
                      <div className="ac-panel-h">
                        <p className="ac-panel-title">Transcript</p>
                        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                          <span className="ac-panel-meta">
                            {project.transcript?.words?.length || 0} words · {project.language || "auto"}
                            {readingStats.wpm > 0 && (
                              <>
                                {" · "}
                                <span style={{ color: readingStats.wpm > 180 ? "#f59e0b" : "var(--ac-text-soft)" }} data-testid="ac-wpm">
                                  {readingStats.wpm} WPM
                                </span>
                                {readingStats.dense > 0 && (
                                  <span style={{ color: "#ef4444", marginLeft: 6 }} title="Captions where chars/sec > 20 — hard to read" data-testid="ac-dense">
                                    · ⚠ {readingStats.dense} dense
                                  </span>
                                )}
                              </>
                            )}
                          </span>
                          <button
                            type="button"
                            className="ac-link"
                            onClick={() => setFindOpen(true)}
                            data-testid="ac-find-replace-open"
                            title="Bulk-replace a word or name across the transcript"
                          >
                            🔎 Find &amp; Replace
                          </button>
                          <button
                            type="button"
                            className="ac-link"
                            onClick={() => srtInput.current?.click()}
                            data-testid="ac-srt-import"
                            title="Replace transcript with an SRT/VTT file"
                          >
                            <FileUp size={12}/> Import SRT
                          </button>
                          <input
                            ref={srtInput}
                            type="file"
                            accept=".srt,.vtt,text/plain"
                            hidden
                            onChange={onSrtPick}
                            data-testid="ac-srt-input"
                          />
                        </div>
                      </div>
                      {findOpen && (
                        <div className="ac-find-row" data-testid="ac-find-row">
                          <input
                            type="text"
                            placeholder="Find…"
                            value={findTerm}
                            onChange={(e) => setFindTerm(e.target.value)}
                            data-testid="ac-find-input"
                            autoFocus
                          />
                          <input
                            type="text"
                            placeholder="Replace with…"
                            value={replaceTerm}
                            onChange={(e) => setReplaceTerm(e.target.value)}
                            data-testid="ac-replace-input"
                          />
                          <button
                            type="button"
                            className="ac-btn ac-btn--ghost"
                            onClick={runFindReplace}
                            disabled={!findTerm}
                            data-testid="ac-find-apply"
                          >Replace all</button>
                          <button
                            type="button"
                            className="ac-btn ac-btn--ghost"
                            onClick={() => { setFindOpen(false); setFindTerm(""); setReplaceTerm(""); }}
                            data-testid="ac-find-close"
                          >Cancel</button>
                        </div>
                      )}
                      <div className="ac-words ac-words--short" data-testid="ac-words">
                        {(project.transcript?.words || []).length === 0 && (
                          <p style={{ color: "var(--ac-text-muted)" }}>No words detected. Try a clearer video.</p>
                        )}
                        {(project.transcript?.words || []).map((w, i) => (
                          <WordChip
                            key={i}
                            index={i}
                            word={w.word}
                            onEdit={editWord}
                            onDelete={deleteWord}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ac-next-banner" data-testid="ac-next-banner">
                  <Wand2 size={18}/>
                  <div>
                    <strong>Step 3 complete!</strong> Tap below to render the final captioned MP4 (Step 4).
                  </div>
                </div>

                <div className="ac-generate-row">
                  <button className="ac-btn ac-btn--ghost" onClick={reset} data-testid="ac-reset">
                    <RotateCcw size={14} /> Start over
                  </button>
                  <button className="ac-btn ac-btn--primary-xl" onClick={generate} data-testid="ac-generate">
                    <Wand2 size={16} /> Generate captioned video →
                  </button>
                </div>
              </motion.div>
            )}

            {/* ── Step 4: Rendering ── */}
            {project && status === "rendering" && (
              <motion.div key="rendering" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="ac-card">
                <div className="ac-progress-hero">
                  <div className="ac-progress-spinner"><Film size={22} /></div>
                  <p className="ac-progress-title">Burning captions into your video…</p>
                  <p className="ac-progress-sub">FFmpeg apna kaam kar raha hai. Aam taur par 20-60 seconds lagte hain.</p>
                  <div className="ac-progress-bar"><span style={{ width: `${project.progress || 90}%` }} /></div>
                </div>
              </motion.div>
            )}

            {/* ── Step 5: Done ── */}
            {project && status === "done" && (
              <motion.div key="done" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="ac-result">
                  <div className="ac-video-wrap" data-testid="ac-video">
                    <video
                      src={`${API}${project.outputs?.mp4}`}
                      controls
                      playsInline
                      preload="metadata"
                    />
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                      <CheckCircle2 size={18} color="#22c55e" />
                      <strong>Captions ready</strong>
                    </div>
                    <div className="ac-download-list">
                      <a className="ac-download" href={`${API}${project.outputs?.mp4}`} target="_blank" rel="noreferrer" data-testid="ac-dl-mp4">
                        <span className="ac-download-icon"><Play size={16} /></span>
                        <div className="ac-download-info">
                          <p className="ac-download-title">Captioned MP4</p>
                          <p className="ac-download-sub">Burned-in, share-ready video</p>
                        </div>
                        <Download size={16} color="rgba(255,255,255,0.7)" />
                      </a>
                      <a className="ac-download" href={`${API}${project.outputs?.srt}`} target="_blank" rel="noreferrer" data-testid="ac-dl-srt">
                        <span className="ac-download-icon" style={{ background: "linear-gradient(135deg, #fb923c, #f97316)" }}><FileText size={16} /></span>
                        <div className="ac-download-info">
                          <p className="ac-download-title">SRT subtitles</p>
                          <p className="ac-download-sub">Upload separately to YouTube / Premiere</p>
                        </div>
                        <Download size={16} color="rgba(255,255,255,0.7)" />
                      </a>
                      {project.outputs?.vtt && (
                        <a className="ac-download" href={`${API}${project.outputs.vtt}`} target="_blank" rel="noreferrer" data-testid="ac-dl-vtt">
                          <span className="ac-download-icon" style={{ background: "linear-gradient(135deg, #a78bfa, #7c3aed)" }}><FileText size={16} /></span>
                          <div className="ac-download-info">
                            <p className="ac-download-title">WebVTT (.vtt)</p>
                            <p className="ac-download-sub">For HTML &lt;track&gt;, YouTube, CapCut web</p>
                          </div>
                          <Download size={16} color="rgba(255,255,255,0.7)" />
                        </a>
                      )}
                      <a className="ac-download" href={`${API}${project.outputs?.ass}`} target="_blank" rel="noreferrer" data-testid="ac-dl-ass">
                        <span className="ac-download-icon" style={{ background: "linear-gradient(135deg, #22c55e, #16a34a)" }}><FileText size={16} /></span>
                        <div className="ac-download-info">
                          <p className="ac-download-title">ASS subtitles</p>
                          <p className="ac-download-sub">Advanced styled subtitle file</p>
                        </div>
                        <Download size={16} color="rgba(255,255,255,0.7)" />
                      </a>
                    </div>
                    <div className="ac-generate-row" style={{ marginTop: 18, justifyContent: "flex-start", gap: 10, flexWrap: "wrap" }}>
                      {project.share_slug && (
                        <button
                          type="button"
                          className="ac-share-action"
                          onClick={copyShareLink}
                          data-testid="ac-share-copy"
                        >
                          <Share2 size={14}/> Copy share link
                        </button>
                      )}
                      <button
                        type="button"
                        className="ac-share-action"
                        onClick={splitIntoScenes}
                        disabled={splitting}
                        data-testid="ac-split-scenes"
                      >
                        {splitting ? <Loader2 size={14} className="ac-spin"/> : <Scissors size={14}/>}
                        {splitting ? "Detecting scenes…" : "Split into scenes"}
                      </button>
                      <button className="ac-btn ac-btn--ghost" onClick={reset} data-testid="ac-new">
                        <RotateCcw size={14} /> Caption another video
                      </button>
                    </div>
                    {sceneClips && sceneClips.length > 0 && (
                      <div className="ac-scenes" data-testid="ac-scene-clips">
                        <p className="ac-extra-label" style={{ marginTop: 18 }}>
                          <Scissors size={11}/> {sceneClips.length} scene clips
                        </p>
                        <div className="ac-scenes-list">
                          {sceneClips.map((c) => (
                            <a
                              key={c.index}
                              href={`${API}${c.url}`}
                              target="_blank"
                              rel="noreferrer"
                              className="ac-scene-clip"
                              data-testid={`ac-scene-${c.index}`}
                            >
                              <span className="ac-scene-num">{c.index}</span>
                              <div className="ac-scene-info">
                                <p>Scene {c.index}</p>
                                <span>{c.start.toFixed(1)}s – {c.end.toFixed(1)}s</span>
                              </div>
                              <Download size={14} color="rgba(255,255,255,0.7)"/>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    {project.is_watermarked && (
                      <p className="ac-wm-badge" data-testid="ac-watermark-result">
                        ⚠️ Watermarked —{" "}
                        <button
                          type="button"
                          className="ac-link"
                          onClick={() => navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
                        >
                          sign in
                        </button>{" "}
                        to render clean exports next time.
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── Failed ── */}
            {project && status === "failed" && (
              <motion.div key="failed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="ac-card" data-testid="ac-failed">
                <div className="ac-progress-hero">
                  <div className="ac-progress-spinner" style={{ background: "conic-gradient(from 0deg, #f87171, #fbbf24, #f87171)" }}><AlertTriangle size={22} /></div>
                  <p className="ac-progress-title">Something went wrong</p>
                  <p className="ac-progress-sub">{project.error || "We couldn't finish your captions."}</p>
                  <div className="ac-generate-row" style={{ justifyContent: "center", marginTop: 20 }}>
                    <button className="ac-btn" onClick={reset} data-testid="ac-failed-reset">
                      <RotateCcw size={14} /> Try again
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <Footer />
    </>
  );
}

// ── Caption overlay (DOM-positioned synced to <video>.currentTime) ──────
function CaptionOverlay({ line, style, t }) {
  const isUpper = !!style.uppercase;
  const isBoxed = style.box === "solid";

  // Choose vertical position
  const positionStyle = {};
  if (style.position === "top") { positionStyle.top = `${style.margin_pct || 6}%`; positionStyle.alignItems = "flex-start"; }
  else if (style.position === "center") { positionStyle.top = "50%"; positionStyle.transform = "translateY(-50%)"; }
  else { positionStyle.bottom = `${style.margin_pct || 8}%`; positionStyle.alignItems = "flex-end"; }

  // Horizontal alignment
  const justifyMap = { left: "flex-start", center: "center", right: "flex-end" };

  return (
    <div
      className="ac-overlay"
      style={{
        ...positionStyle,
        justifyContent: justifyMap[style.alignment] || "center",
      }}
      data-testid="ac-caption-overlay"
    >
      <div
        className="ac-overlay-line"
        style={{
          fontSize: `${style.font_size_pct}cqh`,
          fontFamily: style.font || "FreeSans, system-ui",
          padding: isBoxed ? "0.5em 0.8em" : "0",
          background: isBoxed ? `rgba(0,0,0,${style.box_opacity})` : "transparent",
          borderRadius: isBoxed ? 8 : 0,
          color: style.text_color,
          textTransform: isUpper ? "uppercase" : "none",
          WebkitTextStroke: `${Math.max(0.5, (style.outline_width || 2) / 2)}px ${style.outline_color || "#000"}`,
          paintOrder: "stroke fill",
          textAlign: style.alignment || "center",
          maxWidth: "90%",
        }}
      >
        {line.words.map((w, i) => {
          const isActive = t >= w.start - 0.05 && t <= w.end + 0.05;
          const popped = isActive && style.animation === "pop_scale";
          return (
            <span
              key={i}
              style={{
                color: isActive ? style.highlight_color : style.text_color,
                display: "inline-block",
                transition: "transform 80ms ease-out, color 80ms ease-out",
                transform: popped ? "scale(1.18)" : "scale(1)",
                margin: "0 0.18em",
              }}
            >
              {isUpper ? (w.word || "").toUpperCase() : w.word}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ── Custom controls panel ──────────────────────────────────────────────
function CustomControls({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="ac-custom" data-testid="ac-custom-controls">
      <Field label="Font family">
        <select
          className="ac-select"
          value={value.font || "FreeSans"}
          onChange={(e) => set("font", e.target.value)}
          data-testid="ac-font-select"
        >
          {FONT_FAMILIES.map((f) => (
            <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
          ))}
        </select>
      </Field>
      <Field label="Font size" data-testid="ac-fs">
        <input
          type="range" min="3" max="14" step="0.1"
          value={value.font_size_pct}
          onChange={(e) => set("font_size_pct", parseFloat(e.target.value))}
          data-testid="ac-fs-slider"
        />
        <span className="ac-field-value">{Number(value.font_size_pct).toFixed(1)}%</span>
      </Field>
      <Field label="Words per line">
        <input
          type="range" min="1" max="8" step="1"
          value={value.words_per_line}
          onChange={(e) => set("words_per_line", parseInt(e.target.value, 10))}
          data-testid="ac-wpl-slider"
        />
        <span className="ac-field-value">{value.words_per_line}</span>
      </Field>
      <Field label="Text colour">
        <input type="color" value={value.text_color} onChange={(e) => set("text_color", e.target.value)} data-testid="ac-color-text"/>
      </Field>
      <Field label="Highlight">
        <input type="color" value={value.highlight_color} onChange={(e) => set("highlight_color", e.target.value)} data-testid="ac-color-highlight"/>
      </Field>
      <Field label="Outline">
        <input type="color" value={value.outline_color} onChange={(e) => set("outline_color", e.target.value)} data-testid="ac-color-outline"/>
      </Field>
      <Field label="Outline width">
        <input
          type="range" min="0" max="6" step="0.2"
          value={value.outline_width ?? 2.4}
          onChange={(e) => set("outline_width", parseFloat(e.target.value))}
          data-testid="ac-outline-width"
        />
        <span className="ac-field-value">{Number(value.outline_width ?? 2.4).toFixed(1)}px</span>
      </Field>
      <Field label="Position">
        <div className="ac-pill-group" role="radiogroup" data-testid="ac-position-group">
          {POSITIONS.map((p) => (
            <button
              key={p.key}
              type="button"
              className="ac-pill"
              data-active={value.position === p.key}
              onClick={() => set("position", p.key)}
              data-testid={`ac-position-${p.key}`}
            >{p.label}</button>
          ))}
        </div>
      </Field>
      <Field label="Alignment">
        <div className="ac-pill-group" role="radiogroup" data-testid="ac-align-group">
          {ALIGNMENTS.map((a) => (
            <button
              key={a.key}
              type="button"
              className="ac-pill"
              data-active={(value.alignment || "center") === a.key}
              onClick={() => set("alignment", a.key)}
              data-testid={`ac-align-${a.key}`}
            >{a.label}</button>
          ))}
        </div>
      </Field>
      <Field label="Animation">
        <div className="ac-pill-group" role="radiogroup" data-testid="ac-anim-group">
          {ANIMATIONS.map((a) => (
            <button
              key={a.key}
              type="button"
              className="ac-pill"
              data-active={value.animation === a.key}
              onClick={() => set("animation", a.key)}
              data-testid={`ac-anim-${a.key}`}
            >{a.label}</button>
          ))}
        </div>
      </Field>
      <Field label="Box behind text">
        <label className="ac-switch">
          <input
            type="checkbox"
            checked={value.box === "solid"}
            onChange={(e) => set("box", e.target.checked ? "solid" : "none")}
            data-testid="ac-box-toggle"
          />
          <span>{value.box === "solid" ? "On" : "Off"}</span>
        </label>
      </Field>
      {value.box === "solid" && (
        <>
          <Field label="Box colour">
            <input type="color" value={value.box_color || "#000000"} onChange={(e) => set("box_color", e.target.value)} data-testid="ac-box-color"/>
          </Field>
          <Field label="Box opacity">
            <input
              type="range" min="0.2" max="1" step="0.05"
              value={value.box_opacity}
              onChange={(e) => set("box_opacity", parseFloat(e.target.value))}
              data-testid="ac-box-opacity"
            />
            <span className="ac-field-value">{Math.round((value.box_opacity || 0) * 100)}%</span>
          </Field>
        </>
      )}
      <Field label="Uppercase">
        <label className="ac-switch">
          <input type="checkbox" checked={!!value.uppercase} onChange={(e) => set("uppercase", e.target.checked)} data-testid="ac-uppercase"/>
          <span>{value.uppercase ? "ON" : "OFF"}</span>
        </label>
      </Field>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="ac-field">
      <span className="ac-field-label">{label}</span>
      <div className="ac-field-control">{children}</div>
    </label>
  );
}
